import { z } from 'zod'
import { idSchema, isoDateTimeSchema, minorNonNegativeSchema, optionalText, text } from './common.js'

/**
 * Operating costs: overhead expenses for running the garage, not linked to
 * a specific job. Tracked monthly, with optional recurring entries that expand
 * into one charge per month in the advanced P&L report.
 */
export const OPERATING_COST_TYPES = ['rent', 'utilities', 'insurance', 'licences', 'tools', 'marketing', 'loan', 'other'] as const
export type OperatingCostType = (typeof OPERATING_COST_TYPES)[number]
export const operatingCostTypeSchema = z.enum(OPERATING_COST_TYPES)

export const OPERATING_COST_TYPE_LABELS: Record<OperatingCostType, string> = {
  rent: 'Rent',
  utilities: 'Utilities',
  insurance: 'Insurance',
  licences: 'Licences',
  tools: 'Tools',
  marketing: 'Marketing',
  loan: 'Loan repayment',
  other: 'Other',
}

const operatingCostFields = {
  type: operatingCostTypeSchema,
  /** What this cost was for, e.g. "Monthly office rent". Max 200 characters. */
  description: text(200),
  /** The date this cost applies to, "YYYY-MM-DD". For a recurring cost, this is the first month it applies. */
  date: z.string().min(1),
  amountBeforeVatMinor: minorNonNegativeSchema,
  vatMinor: minorNonNegativeSchema,
  /**
   * Whether this cost recurs monthly. When false, this is a one-time charge.
   * When true, the cost is repeated every month until `endsOn`, inclusive.
   */
  recurringMonthly: z.boolean().default(false),
  /**
   * The last month this cost applies to, "YYYY-MM-DD" format — only meaningful
   * when `recurringMonthly` is true. Null when the cost has no planned end date.
   */
  endsOn: z.string().nullable().default(null),
  /**
   * The date this cost was paid, "YYYY-MM-DD" format. Null when unpaid.
   * Used for cash-basis recognition in the P&L (when the money left the bank).
   */
  paidAt: z.string().nullable().default(null),
  notes: optionalText(1000),
}

function validateRecurringDates(
  value: { date: string; recurringMonthly: boolean; endsOn: string | null },
  ctx: z.RefinementCtx,
) {
  // endsOn must be >= date when set
  if (value.endsOn && value.endsOn < value.date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endsOn'],
      message: 'End date must be on or after the start date',
    })
  }
  // endsOn must be null when not recurring
  if (!value.recurringMonthly && value.endsOn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endsOn'],
      message: 'End date is only meaningful for recurring costs',
    })
  }
}

export const operatingCostInputSchema = z.object(operatingCostFields).superRefine(validateRecurringDates)
export type OperatingCostInput = z.infer<typeof operatingCostInputSchema>

export const operatingCostSchema = z.object({
  ...operatingCostFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type OperatingCost = z.infer<typeof operatingCostSchema>

// List items use the same type as the full entity
export type OperatingCostListItem = OperatingCost

/**
 * Query parameters for listing operating costs.
 * Supports filtering by type, date range, and text search.
 */
export const operatingCostListQuerySchema = z.object({
  type: operatingCostTypeSchema.optional(),
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
})
export type OperatingCostListQuery = z.infer<typeof operatingCostListQuerySchema>
